import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { ExternalLink, AlertTriangle, CheckCircle, XCircle, Clock, Shield, Zap } from 'lucide-react';
import { getLicenseTyper } from '@/helpers/common';

interface ComponentDetailsModalProps {
  selectedComponent: any;
  isDetailsModalOpen: boolean;
  setIsDetailsModalOpen: (open: boolean) => void;
}

export function ComponentDetailsModal({ selectedComponent, isDetailsModalOpen, setIsDetailsModalOpen }: ComponentDetailsModalProps) {
  return (
    <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
      <DialogContent className="max-w-4xl">
        {selectedComponent && (
          <>
            <DialogHeader>
              <div className="flex justify-between items-start">
                <DialogTitle className="text-2xl font-semibold">{selectedComponent.name}</DialogTitle>
                <Badge style={{ whiteSpace: "wrap" }}
                  className={
                    'bg-muted text-muted-foreground'
                  }
                >
                  {selectedComponent.maintenance_status}
                </Badge>

              </div>
              <DialogDescription className="text-base mt-2">
                {selectedComponent.description || 'No description available'}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
           
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Component Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Version:</span>
                    <span className="font-medium">{selectedComponent.version || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Homepage URL:</span>
                    <a
                      href={selectedComponent.homepageurl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center"
                    >
                      Visit <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">License:</span>
                    <span className="font-medium">{selectedComponent.license_spdx_identifier ?? selectedComponent.licensename ?? 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">License Category:</span>
                    <span className="font-medium">{getLicenseTyper(selectedComponent.licensecategory) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deprecated:</span>
                    <div>
                      {selectedComponent.isdeprecated ?
                        <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-xs flex items-center">
                          Yes
                        </Badge> :
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs flex items-center">
                          No
                        </Badge>
                      }
                    </div>
                  </div>
                
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Risk Assessment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">CVSS Score:</span>
                      <span className={selectedComponent.avg_cvss_score >= 8.1 ? 'text-red-600 dark:text-red-400 font-semibold' :
                        selectedComponent.avg_cvss_score >= 6.1 ? 'text-orange-600 dark:text-orange-400 font-semibold' :
                        selectedComponent.avg_cvss_score >= 4.1 ? 'text-yellow-600 dark:text-yellow-400 font-semibold' :
                          'text-green-600 dark:text-green-400 font-semibold'}>
                        {(selectedComponent.avg_cvss_score || 0).toFixed(2)}
                      </span>
                    </div>

                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">EPSS Score:</span>
                      <span className={selectedComponent.avg_epss_score >= 0.7 ? 'text-red-600 dark:text-red-400 font-semibold' :
                        selectedComponent.avg_epss_score >= 0.4 ? 'text-orange-600 dark:text-orange-400 font-semibold' :
                          'text-green-600 dark:text-green-400 font-semibold'}>
                        {(selectedComponent.avg_epss_score || 0).toFixed(2)}
                      </span>
                    </div>

                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">Risk Score:</span>
                      <span className={selectedComponent.risk_score >= 90 ? 'text-red-600 dark:text-red-400 font-semibold' :
                        selectedComponent.risk_score >= 70 ? 'text-orange-600 dark:text-orange-400 font-semibold' :
                        selectedComponent.risk_score >= 40 ? 'text-yellow-600 dark:text-yellow-400 font-semibold' :
                          'text-green-600 dark:text-green-400 font-semibold'}>
                        {(selectedComponent.risk_score || 0).toFixed(2)}
                      </span>
                    </div>


                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-muted-foreground">Days Since Last Commit:</span>
                      <span >
                        {selectedComponent.dayssincelastcommit ?? '0'} days
                      </span>
                    </div>
                    {([
                      ['Stars', selectedComponent.githubstars],
                      ['Contributors', selectedComponent.githubcontributors],
                      ['Forks', selectedComponent.githubforks],
                    ] as [string, number | null | undefined][]).map(([label, val]) => (
                      <div key={label} className="flex justify-between mb-1">
                        <span className="text-muted-foreground">{label}:</span>
                        <span>{typeof val === 'number' ? val.toLocaleString() : '-'}</span>
                      </div>
                    ))}

                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Security Vulnerabilities</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                        {selectedComponent.total_vulnerabilities || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Vulnerabilities</div>
                    </div>
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                        {selectedComponent.exploit_db_available_count || '0'}
                      </div>
                      <div className="text-xs text-muted-foreground">Exploit Available</div>
                    </div>
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <div className="text-lg font-semibold text-primary">
                        {selectedComponent.fix_available_count || '0'}
                      </div>
                      <div className="text-xs text-muted-foreground">Fix Available</div>
                    </div>
                    <div className="text-center p-3 bg-muted rounded-lg">
                      <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                        {(selectedComponent.total_license_issues || '0')}
                      </div>
                      <div className="text-xs text-muted-foreground">Total License Issues</div>
                    </div>
                  </div>

                  {selectedComponent.vulnerabilities && selectedComponent.vulnerabilities.length > 0 && (
                    <div className="space-y-2">
                      {selectedComponent.vulnerabilities.map((vuln: any, i: number) => (
                        <div key={i} className="bg-muted p-3 rounded-md border-l-4 border-red-500 dark:border-red-400">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="font-medium">{vuln.cveId || `Vulnerability ${i + 1}`}</span>
                              <Badge className="ml-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-xs">
                                {vuln.severity || 'High'}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              CVSS: {vuln.avg_cvss_score?.toFixed(2) || 'N/A'}
                            </div>
                          </div>
                          <p className="text-muted-foreground text-sm mb-2">
                            {vuln.description || 'No description available'}
                          </p>
                          <div className="flex gap-2">
                            {vuln.exploitAvailable && (
                              <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 text-xs flex items-center">
                                <Zap className="w-3 h-3 mr-1" /> Exploit Available
                              </Badge>
                            )}
                            {vuln.fixAvailable && (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs flex items-center">
                                <CheckCircle className="w-3 h-3 mr-1" /> Fix Available
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

         

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsDetailsModalOpen(false)}>
                Close
              </Button>
              <Button
                onClick={() => window.open(selectedComponent.homepageurl, '_blank', 'noopener,noreferrer')}
              >
                Visit Project Site <ExternalLink className="w-4 h-4 ml-1" />
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}